import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING } from "@constants";
import { formatDate, ONE_WEEK_RANGE, today } from "@helpers/utils";
import { getIntervalsForDateRange, initializeBuilding } from "@store/appdata";
import moment from "moment";

const SLICE = "energyData";
const ENERGY_USE = "Energy Use";
const TRENDING = "Trending";

const action = (name) => `${SLICE}/${name}`;

const getDatapoint = (datapoints, matchFn) => {
  let totalizer = undefined;
  for (const key of ["EIMS", "IAQMS", "BMS"]) {
    const toSearch = datapoints[key];
    totalizer = toSearch.find(matchFn);
    if (totalizer) break;
  }
  return totalizer;
};

const getDatapoints = (datapoints, matchFn) => {
  const retVal = [];
  for (const key of ["EIMS", "IAQMS", "BMS"]) {
    const toSearch = datapoints[key];
    toSearch.filter(matchFn).forEach((it) => retVal.push(it));
  }
  return retVal;
};

export const initializePage = createAsyncThunk(
  action("initializePage"),
  async ({ siteId, initialSteam }, { dispatch, getState }) => {
    await dispatch(initializeBuilding({ siteId }));
    const appData = getState().appData;
    const forBuilding = appData.buildingData[siteId];
    const totalizerField = initialSteam
      ? "steam_datapoint_id"
      : "electric_datapoint_id";
    const totalizer = forBuilding.info.data[totalizerField];

    let selectedAccount = undefined;
    let selectedAccountTotalizer = undefined;
    let accountDatapoints = undefined;

    if (totalizer) {
      const datapoints = forBuilding.datapoints;
      selectedAccountTotalizer = getDatapoint(
        datapoints,
        (it) => it.datapoint_id === totalizer,
      );
      if (selectedAccountTotalizer) {
        selectedAccount = forBuilding.accounts.data.find(
          (it) => it.id === selectedAccountTotalizer.account_id,
        );
        accountDatapoints = getDatapoints(
          datapoints,
          (it) => it.account_id === selectedAccountTotalizer.account_id,
        );
      }
    }

    return {
      response: {
        message: "initialized",
        datapoints: accountDatapoints,
        selectedAccount,
        selectedAccountTotalizer,
      },
    };
  },
);

export const changeAccount = createAsyncThunk(
  action("changeAccount"),
  async (payload, { getState }) => {
    const { account } = payload;
    const { appData } = getState();
    const datapoints = appData.buildingData[account.siteId].datapoints;
    const accountDatapoints = getDatapoints(
      datapoints,
      (it) => it.account_id === account.id,
    );
    const totalizer = accountDatapoints.find(
      (it) => it.account_totalizer === 1,
    );

    return { totalizer, datapoints: accountDatapoints, account };
  },
);

export const loadDataForRange = createAsyncThunk(
  action("loadDataForRange"),
  async () => {
    return {
      response: {},
    };
  },
);

export const compareDatapointPeriod = createAsyncThunk(
  action("compareDatapointPeriod"),
  async ({ datapointId, dateRange, fetchKey }, { getState, dispatch }) => {
    const options = {
      fetchKey,
      id: datapointId,
      startdate: formatDate(dateRange[0]),
      stopdate: formatDate(dateRange[1]),
    };

    await dispatch(getIntervalsForDateRange({ options }));
    const comparisonIntervalData =
      getState().appData.intervalDataByDate[fetchKey];

    return {
      comparisonIntervalData,
    };
  },
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    buildingData: {},
    status: IDLE,
  },
  reducers: {
    loadAccrual: (state, action) => {
      const { component, mode, siteId, tabName } = action.payload;

      const startOfMonth = new Date(today().getTime());
      startOfMonth.setDate(1);

      const endOfMonth = moment(startOfMonth).endOf("month").toDate();

      state.buildingData[siteId][component] = {
        ...state.buildingData[siteId][component],
        cacheKey: Date.now(),
        dateRange: [startOfMonth, endOfMonth],
        mode,
        tabName,
      };
    },
    removeComparison: (state, action) => {
      const { component, siteId } = action.payload;

      state.buildingData[siteId][component] = {
        ...state.buildingData[siteId][component],
        comparisonDateRange: undefined,
        comparisonIntervalData: undefined,
      };
    },
    toggleTrendingView: (state, action) => {
      const { component, isEnabled, siteId, tabName } = action.payload;

      const newState = {
        ...state.buildingData[siteId][component],
        cacheKey: Date.now(),
        mode: isEnabled ? TRENDING : ENERGY_USE,
        tabName,
      };

      if (isEnabled) {
        newState.comparisonDateRange = undefined;
        newState.comparisonIntervalData = undefined;
        newState.isShowSubmeters = false;
      }

      state.buildingData[siteId][component] = newState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeAccount.fulfilled, (state, action) => {
        const { payload } = action;
        const { meta } = action;
        const { account, component, isShowSubmeters } = meta.arg;
        const { siteId } = account;
        const newState = {
          ...state.buildingData[siteId][component],
          cacheKey: Date.now(),
          datapoints: payload.datapoints,
          isShowSubmeters,
          selectedAccount: payload.account,
          selectedAccountTotalizer: payload.totalizer,
        };
        state.buildingData[siteId][component] = newState;

        if (isShowSubmeters) {
          newState.mode = ENERGY_USE;
        }
      })
      .addCase(compareDatapointPeriod.fulfilled, (state, action) => {
        const { payload } = action;
        const { meta } = action;
        const { component, dateRange, siteId } = meta.arg;

        state.buildingData[siteId][component] = {
          ...state.buildingData[siteId][component],
          comparisonDateRange: dateRange,
          comparisonIntervalData: payload.comparisonIntervalData,
          mode: ENERGY_USE,
        };
      })
      .addCase(initializePage.pending, (state, action) => {
        const { meta } = action;
        const { component, mode, siteId } = meta.arg;

        let forBuilding = state.buildingData[siteId];
        if (!forBuilding) {
          forBuilding = {};
          state.buildingData[siteId] = forBuilding;
        }
        forBuilding[component] = {
          dateRange: ONE_WEEK_RANGE,
          isShowSubmeters: false,
          tabName: undefined,
          mode,
        };
        state.status = LOADING;
      })
      .addCase(initializePage.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { response } = payload;
        const {
          datapoints,
          message,
          selectedAccount,
          selectedAccountTotalizer,
        } = response;

        const { component, siteId } = meta.arg;

        state.buildingData[siteId][component] = {
          ...state.buildingData[siteId][component],
          datapoints,
          message,
          selectedAccount,
          selectedAccountTotalizer,
        };

        state.status = IDLE;
      })
      .addCase(loadDataForRange.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(loadDataForRange.fulfilled, (state, action) => {
        const { meta } = action;
        const { component, dates, siteId } = meta.arg;

        state.buildingData[siteId][component] = {
          ...state.buildingData[siteId][component],
          dateRange: dates,
        };

        state.status = IDLE;
      });
  },
});

export const { loadAccrual, removeComparison, toggleTrendingView } =
  slice.actions;

export const INITIAL_STATE = slice.getInitialState();

export default slice.reducer;
